import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Download, FileText, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ErrorAlert } from '../../components/ui/ErrorAlert';

interface DataExportRequest {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired';
  format: 'json' | 'pdf' | 'zip';
  created_at: string;
  download_url: string | null;
  url_expires_at: string | null;
  data_summary: {
    appointments_count?: number;
    invoices_count?: number;
    questionnaires_count?: number;
    journal_entries_count?: number;
    documents_count?: number;
    sms_logs_count?: number;
    quotes_count?: number;
  } | null;
  error_message: string | null;
}

export function PortalDataExport() {
  const queryClient = useQueryClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch current user's patient record
  const { data: patient } = useQuery({
    queryKey: ['portal-patient'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('patients')
        .select('id, clinic_id')
        .eq('portal_user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch export requests
  const { data: exportRequests, isLoading } = useQuery({
    queryKey: ['data-export-requests', patient?.id],
    queryFn: async () => {
      if (!patient?.id) return [];

      const { data, error } = await supabase
        .from('data_export_requests')
        .select('*')
        .eq('patient_id', patient.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as DataExportRequest[];
    },
    enabled: !!patient?.id,
  });

  // Request new export mutation
  const requestExportMutation = useMutation({
    mutationFn: async () => {
      if (!patient?.id) throw new Error('Patient ID not found');

      const { data, error } = await supabase.rpc('request_data_export', {
        p_patient_id: patient.id,
        p_format: 'zip',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-export-requests'] });
      setShowConfirmModal(false);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to request data export');
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Klar til download';
      case 'failed':
        return 'Fejlet';
      case 'processing':
        return 'Behandler...';
      case 'pending':
        return 'Afventer';
      case 'expired':
        return 'Udløbet';
      default:
        return status;
    }
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mine Data (GDPR)</h1>
        <p className="text-gray-600 mt-2">
          Du har ret til at få en kopi af dine personlige data. Vi behandler din anmodning inden for 30 dage.
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorAlert message={error} />
        </div>
      )}

      {/* Info Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Hvad inkluderer eksporten?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Personlige oplysninger</li>
              <li>• Aftaler og bookinger</li>
              <li>• Fakturaer</li>
              <li>• Spørgeskemaer og svar</li>
              <li>• Journalnotater</li>
              <li>• Dokumenter (metadata)</li>
              <li>• SMS og email kommunikation</li>
            </ul>
            <button
              onClick={() => setShowInfoModal(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2"
            >
              Læs mere om dine rettigheder
            </button>
          </div>
        </div>
      </div>

      {/* Request New Export Button */}
      <div className="mb-6">
        <Button
          onClick={() => setShowConfirmModal(true)}
          disabled={requestExportMutation.isPending}
          className="w-full sm:w-auto"
        >
          <Download className="w-4 h-4 mr-2" />
          Anmod om Data Eksport
        </Button>
      </div>

      {/* Export Requests List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Tidligere Anmodninger</h2>

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Indlæser...</div>
        ) : !exportRequests || exportRequests.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Du har ikke anmodet om data eksport endnu.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {exportRequests.map((request) => {
              const expired = request.url_expires_at && isExpired(request.url_expires_at);
              const canDownload = request.status === 'completed' && request.download_url && !expired;

              return (
                <div
                  key={request.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {getStatusIcon(expired ? 'expired' : request.status)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">
                            {getStatusText(expired ? 'expired' : request.status)}
                          </span>
                          <span className="text-sm text-gray-500">•</span>
                          <span className="text-sm text-gray-500">
                            {formatDate(request.created_at)}
                          </span>
                        </div>

                        {request.data_summary && request.status === 'completed' && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span>Inkluderer: </span>
                            {Object.entries(request.data_summary)
                              .filter(([_, count]) => count && count > 0)
                              .map(([key, count]) => {
                                const labels: { [key: string]: string } = {
                                  appointments_count: `${count} aftaler`,
                                  invoices_count: `${count} fakturaer`,
                                  questionnaires_count: `${count} spørgeskemaer`,
                                  journal_entries_count: `${count} journalnotater`,
                                  documents_count: `${count} dokumenter`,
                                  sms_logs_count: `${count} SMS`,
                                  quotes_count: `${count} tilbud`,
                                };
                                return labels[key];
                              })
                              .join(', ')}
                          </div>
                        )}

                        {request.error_message && (
                          <p className="mt-2 text-sm text-red-600">{request.error_message}</p>
                        )}

                        {request.url_expires_at && !expired && request.status === 'completed' && (
                          <p className="mt-1 text-xs text-gray-500">
                            Udløber: {formatDate(request.url_expires_at)}
                          </p>
                        )}

                        {expired && (
                          <p className="mt-1 text-xs text-gray-500">
                            Download link er udløbet. Anmod om en ny eksport.
                          </p>
                        )}
                      </div>
                    </div>

                    {canDownload && (
                      <a
                        href={request.download_url!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-4 flex-shrink-0"
                      >
                        <Button size="sm">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Modal
          onClose={() => !requestExportMutation.isPending && setShowConfirmModal(false)}
          title="Anmod om Data Eksport"
        >
        <div className="space-y-4">
          <p className="text-gray-700">
            Vi vil samle alle dine personlige data og sende dig en downloadlink via email.
            Dette kan tage op til 24 timer.
          </p>
          <p className="text-gray-700">
            Download linket vil være gyldigt i 7 dage af sikkerhedsmæssige årsager.
          </p>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
              disabled={requestExportMutation.isPending}
            >
              Annuller
            </Button>
            <Button
              onClick={() => requestExportMutation.mutate()}
              disabled={requestExportMutation.isPending}
            >
              {requestExportMutation.isPending ? 'Sender anmodning...' : 'Anmod om Eksport'}
            </Button>
          </div>
        </div>
        </Modal>
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <Modal
          onClose={() => setShowInfoModal(false)}
          title="Dine GDPR Rettigheder"
        >
        <div className="space-y-4 text-gray-700">
          <div>
            <h4 className="font-semibold mb-2">Ret til indsigt (GDPR Artikel 15)</h4>
            <p className="text-sm">
              Du har ret til at få bekræftet, om vi behandler personoplysninger om dig,
              og i givet fald få adgang til disse oplysninger.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Hvad inkluderer eksporten?</h4>
            <ul className="text-sm space-y-1">
              <li>• Personlige oplysninger (navn, kontaktinfo, CPR)</li>
              <li>• Alle aftaler og bookinger med detaljer</li>
              <li>• Fakturaer og betalingshistorik</li>
              <li>• Besvarede spørgeskemaer</li>
              <li>• Journalnotater fra klinikken</li>
              <li>• Metadata om uploadede dokumenter</li>
              <li>• SMS og email kommunikation</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Format og Sikkerhed</h4>
            <p className="text-sm">
              Dataene leveres i maskinlæsbart format (JSON) pakket i en sikker ZIP-fil.
              Download linket udløber efter 7 dage af sikkerhedsmæssige årsager.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Behandlingstid</h4>
            <p className="text-sm">
              Vi behandler din anmodning inden for 30 dage som påkrævet af GDPR.
              De fleste anmodninger behandles inden for 24 timer.
            </p>
          </div>

          <div className="pt-4">
            <Button onClick={() => setShowInfoModal(false)} className="w-full">
              Forstået
            </Button>
          </div>
        </div>
        </Modal>
      )}
    </div>
  );
}
